import { useId, type ReactNode } from "react";
import type { DialerSource, TaskRow, TasksQuery } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { dialerKeys, savedDialerSessionQuery } from "../dialer/dialer-queries";
import { useOptionalDialerSession } from "../dialer/dialer-session";
import { callBlockOf, OtherSessionDialog, savedText, startErrorText, useDialerStart } from "../dialer/dialer-start";
import { sourceText } from "../dialer/dialer-texts";
import { useSourcePeople } from "../dialer/source-people";
import { Button } from "../design/components/Button";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { StatusPill } from "../design/components/StatusPill";
import type { Tone } from "../design/components/tone";
import { messageForError } from "../i18n/errors";
import { formatCount, formatNumericDateTime } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { outcomeLabel } from "../leads/lead-display";
import { leadOwnerOptionsQuery, useLeadView } from "../leads/lead-queries";
import { cityCell, latestCallCell, statusCell } from "../leads/LeadTable";
import { taskScopeOf, useAccess } from "../session/access";
import { tabKey } from "../session/tab-context";
import { canCallFromTasks, useCallFromTasksBlock } from "./call-from-tasks";
import { kindParamOf, leadCardSearchOf, TASK_VIEW_PARAMS, taskSourceParam, tasksListQueryOf, type TasksListQuery, type TasksSearch, type TaskViewParam } from "./task-search";
import { refreshTasks, tasksPagesQuery } from "./tasks-queries";
import "../design/components/page-header.css";
import "../leads/lead-list.css";
import "./tasks.css";

// «Le cose da fare» (piano M5, Task 18; mock-up `task.html`, guida dei Task; D12, D16, D17): le quattro schede «Oggi» (con gli
// scaduti in cima, in rosso), «Scaduti», «Prossimi giorni» e «Completati», ognuna con il suo numero; «Cosa», «Di chi» (soltanto
// per chi vede i Task di tutti, con la colonna «Assegnato a») e «Ordina», tutti nell'indirizzo. Una riga per richiamo: quando, che
// cosa, il Lead con il salone, e le celle «Città», «Status» e «Ultima chiamata» dell'elenco dei Lead; «Chiama» apre la scheda e fa
// partire la chiamata (`useCallFromTasks`; spento, con il motivo, finché la chiamata del browser è occupata), il nome del Lead e
// «Apri» la aprono soltanto. Qui ci sono soltanto i Task presi a mano (D12): lo dice la nota sotto il titolo. Sotto i 720 px di
// contenuto ogni riga è una scheda, senza scorrimento di lato.
//
// «Inizia a chiamare» (piano M5, Task 21; mock-up `task.html`; questione 268) avvia il Power Dialer del guscio sui richiami di
// «Oggi» o di «Scaduti» della persona di «Di chi» (lo User i suoi), con le stesse regole e le stesse parole della Smart List
// (`dialer-start.tsx`): sotto, quante telefonate e da chi si parte, con «Tutto il team» l'avviso che si chiamerebbero anche i Task
// dei colleghi; spento, con il perché, su «Prossimi giorni» e «Completati», senza telefonate e dove non si chiama (la «Guarda
// come…», il contesto di piattaforma, senza «Chiamare e usare la postazione»). Con la sessione interrotta di quella scheda e di
// quella persona, «Riprendi a chiamare» e «Ricomincia da capo»; con quella viva, «Sessione in corso su …» e «Torna alla scheda»
// (ruling Q47). Quando c'è una sessione dai Task di quella scheda e persona, il numero accanto al nome è il posto nella cascata
// (dal server, `position`), qualunque siano l'ordine e i filtri mostrati (questione 338).

const VIEWS: readonly TaskViewParam[] = ["oggi", "scaduti", "prossimi", "completati"];

/** «Fatta adesso»: un richiamo fatto da meno di mezz'ora, rispetto a quando l'agenda è stata letta (questione aperta 329). */
const DONE_NOW_MS = 30 * 60_000;

export function TasksPage() {
  const t = useT();
  const { locale } = useLocale();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const search = useSearch({ from: "/autenticato/guscio/azienda/task" });
  const navigate = useNavigate({ from: "/task" });
  const baseId = useId();
  // Con la chiamata del browser occupata, «Chiama» delle righe è spento con il motivo di «Chiama» della scheda (M4), detto una volta.
  const blocked = useCallFromTasksBlock();
  const busyId = `${baseId}-busy`;
  const scope = taskScopeOf(access, "view");
  const canView = scope !== "none";
  // «Di chi» e «Assegnato a» soltanto per chi vede i Task di tutti (D17), con la regola del server.
  const byPerson = scope === "all";
  const query = tasksListQueryOf(search, byPerson);
  const pages = useInfiniteQuery({ ...tasksPagesQuery(tab, query, view), enabled: canView, placeholderData: keepPreviousData });
  const people = useQuery({ ...leadOwnerOptionsQuery(tab), enabled: canView && byPerson });
  const timeZone = access.context.settings.timeZone;
  const counts = pages.data?.pages[0]?.counts;
  const shown = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  // La scheda di un Lead aperta da qui sa dove tornare: la stessa scheda e la stessa persona.
  const source = taskSourceParam({ ...(search.vista ? { vista: search.vista } : {}), ...(byPerson && search.chi ? { chi: search.chi } : {}) });

  // --- «Inizia a chiamare», «Riprendi a chiamare», «Ricomincia da capo» (piano M5, Task 21; `dialer-start.tsx`) ---
  const queryClient = useQueryClient();
  const shellDialer = useOptionalDialerSession();
  const callBlock = callBlockOf(t, access, tab);
  // Chi chiama da questa scheda: soltanto allora si leggono la sessione salvata e la cascata (mai nella vista né dalla piattaforma).
  const caller = canView && callBlock === null && shellDialer !== null ? tab : null;
  const me = tab.kind === "membership" ? tab.membershipId : null;
  const cascadeView = query.view === "today" || query.view === "overdue" ? query.view : null;
  // «Di chi»: la persona scelta o tutto il team per chi vede i Task di tutti; lo User soltanto i suoi.
  const cascadeWho = byPerson ? (query.who ?? "all") : me;
  const cascadeSource: DialerSource | null = cascadeView !== null && cascadeWho !== null ? { type: "task_view", view: cascadeView, who: cascadeWho } : null;
  const savedQuery = useQuery(savedDialerSessionQuery(cascadeSource !== null ? caller : null, cascadeSource));
  const sourcePeople = useSourcePeople(tab, cascadeSource);
  const { liveHere, starting, startError, startCalling, other } = useDialerStart({
    caller,
    source: cascadeSource,
    // La sessione della barra finisce mentre la pagina è aperta: quella salvata può essere lei, e i posti cambiano.
    onLiveEnded: () => {
      if (caller === null) return;
      void queryClient.invalidateQueries({ queryKey: dialerKeys.savedAll(tabKey(caller)) });
      refreshTasks(queryClient, caller);
    },
    onRefused: () => void savedQuery.refetch(),
    // Tornando qui, le righe portano il posto nella cascata.
    onStarted: () => refreshTasks(queryClient, tab),
  });
  // La cascata: tutti i richiami della scheda e della persona nel loro ordine, qualunque siano l'ordine e i filtri mostrati; disco e
  // demo non si chiamano in cascata (TASK-10, R7). Con «Telefonate» e l'ordine di partenza è la stessa lettura della pagina.
  const cascadeQuery: TasksListQuery = { ...query, kind: "calls", sort: "due" };
  const cascade = useInfiniteQuery({ ...tasksPagesQuery(tab, cascadeQuery, view), enabled: caller !== null && cascadeSource !== null, placeholderData: keepPreviousData });
  const cascadeFirst = cascade.data && !cascade.isPlaceholderData ? cascade.data.pages[0] : undefined;
  const cascadeTotal = cascadeFirst?.total;
  const firstRow = cascadeTotal !== undefined && cascadeTotal > 0 ? cascadeFirst?.rows[0] : undefined;

  // Il primo motivo che vale, poi da chi si parte (o, con la sessione salvata, da chi si riprende); con la sessione viva di questa
  // scheda e persona, la riga della barra.
  let startBlock = liveHere ? t("dialer.restored", { source: sourceText(t, liveHere, sourcePeople) }) : callBlock;
  if (startBlock === null && cascadeView === null) startBlock = t("tasks.start.offView");
  // Con «Cosa» su Disco o Demo «Inizia a chiamare» è spento e dice perché (TASK-10).
  if (startBlock === null && (query.kind === "disco" || query.kind === "demo")) startBlock = t("appointments.tasks.offAppointments");
  if (startBlock === null && cascadeTotal === 0) startBlock = t("tasks.start.offEmpty");
  const savedSession = caller !== null && cascadeSource !== null ? (savedQuery.data ?? null) : null;
  // Finché non si sa se c'è una sessione salvata, niente parte: «Inizia a chiamare» la ricomincerebbe da capo (ruling Q6).
  const savedKnown = caller === null || cascadeSource === null || savedQuery.isSuccess;
  let startLine: string | null = startBlock;
  if (startLine === null && savedKnown) {
    if (savedSession) startLine = savedText(t, locale, timeZone, savedSession, t("tasks.start.offEmpty"));
    else if (firstRow && cascadeTotal !== undefined) {
      const lead = firstRow.lead.displayName ?? firstRow.lead.salonName ?? "—";
      const from = cascadeTotal === 1 ? t("tasks.start.fromOne", { lead }) : t("tasks.start.from", { lead, count: formatCount(locale, cascadeTotal) });
      startLine = byPerson && cascadeWho === "all" ? `${from} ${t("tasks.start.teamWarning")}` : from;
    }
  }
  const startMode = savedSession ? "resume" : "start";
  const startOff = startBlock !== null || !savedKnown || firstRow === undefined || (savedSession !== null && savedSession.next === null) || starting !== null;
  const restartOff = startBlock !== null || cascadeTotal === undefined || starting !== null;
  const startReasonId = `${baseId}-start`;
  const startDescribedBy = startLine === null ? undefined : startReasonId;

  function update(change: Partial<TasksSearch>) {
    void navigate({ search: (previous) => ({ ...previous, ...change }), replace: true });
  }

  const viewOptions = VIEWS.map((id) => ({
    value: id,
    label: t(`tasks.views.${TASK_VIEW_PARAMS[id]}`),
    ...(counts ? { count: formatCount(locale, counts[TASK_VIEW_PARAMS[id]]) } : {}),
  }));
  const kindOptions = [
    { value: "tutto", label: t("tasks.kinds.all") },
    { value: "telefonate", label: t("tasks.kinds.calls") },
    { value: "disco", label: t("appointments.kind.disco") },
    { value: "demo", label: t("appointments.kind.demo") },
  ];
  const sortOptions = [
    { value: "fare", label: t("tasks.sorts.due") },
    { value: "priorita", label: t("tasks.sorts.priority") },
    { value: "lead", label: t("tasks.sorts.lead") },
    { value: "status", label: t("tasks.sorts.status") },
  ];
  // Le persone che possono avere Lead e Task; una persona dell'indirizzo che non c'è (sospesa, un link di prima) resta scelta,
  // con il nome che le righe le danno.
  const personOptions = [{ value: "all", label: t("tasks.wholeTeam") }, ...(people.data ?? []).map((person) => ({ value: person.membershipId, label: person.displayName }))];
  if (search.chi !== undefined && !personOptions.some((option) => option.value === search.chi)) {
    const named = shown?.find((row) => row.task.assignee.membershipId === search.chi)?.task.assignee.displayName;
    personOptions.push({ value: search.chi, label: named ?? search.chi });
  }

  let results: ReactNode;
  if (!canView) {
    results = <Reason tone="warning">{t("errors.permission_denied")}</Reason>;
  } else if (shown === null) {
    results = pages.isError ? (
      <TasksProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-tasks__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (shown.length === 0) {
    results = (
      <div className="g-tasks__empty">
        <p>
          <strong>{t(`tasks.empty.${query.view}`)}</strong>
        </p>
        <p>{t("tasks.emptyHint")}</p>
      </div>
    );
  } else {
    const done = query.view === "done";
    const callable = !done && canCallFromTasks(access, tab);
    results = (
      <>
        {pages.isError ? <TasksProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        {callable && blocked !== null ? (
          <Reason tone="warning" id={busyId} className="g-tasks__busy">
            {blocked}
          </Reason>
        ) : null}
        <div className="g-tasks__table">
          <DataTable caption={t(`tasks.views.${query.view}`)} captionHidden>
            <thead>
              <tr>
                <th scope="col">{t("tasks.columns.when")}</th>
                <th scope="col">{t("tasks.columns.what")}</th>
                <th scope="col">{t("tasks.columns.lead")}</th>
                <th scope="col">{t("tasks.columns.city")}</th>
                <th scope="col">{t("tasks.columns.status")}</th>
                <th scope="col">{t("tasks.columns.latestCall")}</th>
                {byPerson ? <th scope="col">{t(done ? "tasks.columns.owner" : "tasks.columns.assignee")}</th> : null}
                <th scope="col">
                  <span className="g-visually-hidden">{t("tasks.columns.action")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <TaskRowView
                  key={row.task.id}
                  row={row}
                  view={query.view}
                  byPerson={byPerson}
                  callable={callable}
                  busyReasonId={blocked === null ? null : busyId}
                  source={source}
                  timeZone={timeZone}
                  readAt={pages.dataUpdatedAt}
                />
              ))}
            </tbody>
          </DataTable>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {t("tasks.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="g-tasks">
      <div className="g-page-head">
        <div>
          <p className="g-page-head__eyebrow">{t("shell.nav.tasks")}</p>
          <div className="g-tasks__title">
            <h1 className="g-page-head__title">{t("tasks.title")}</h1>
            {counts ? (
              <span className="g-tasks__count" role="status">
                {t("tasks.todayCount", { count: formatCount(locale, counts.today) })}
              </span>
            ) : null}
          </div>
          <p className="g-tasks__note">{t("tasks.onlyManual")}</p>
        </div>
        {canView ? (
          <div className="g-tasks__start">
            <div className="g-tasks__start-actions">
              {liveHere?.current ? (
                <Button
                  variant="primary"
                  aria-describedby={startReasonId}
                  onClick={() => {
                    if (liveHere.current) void navigate({ to: "/lead/$leadId", params: { leadId: liveHere.current.id }, search: leadCardSearchOf(liveHere.source) });
                  }}
                >
                  {t("dialer.backToLead")}
                </Button>
              ) : null}
              {liveHere === null && savedSession ? (
                <Button
                  disabled={restartOff}
                  aria-describedby={startDescribedBy}
                  aria-busy={starting === "restart" ? "true" : undefined}
                  onClick={() => void startCalling("restart")}
                >
                  {t("smartLists.page.restart")}
                </Button>
              ) : null}
              {liveHere === null ? (
                <Button
                  variant="primary"
                  disabled={startOff}
                  aria-describedby={startDescribedBy}
                  aria-busy={starting === startMode ? "true" : undefined}
                  onClick={() => void startCalling(startMode)}
                >
                  <Icon name="phone" />
                  {savedSession ? t("smartLists.page.resume") : t("smartLists.page.start")}
                </Button>
              ) : null}
            </div>
            {startLine !== null ? <Reason id={startReasonId}>{startLine}</Reason> : null}
            {caller !== null && cascadeSource !== null && liveHere === null && savedQuery.isError ? (
              <div className="g-tasks__start-problem">
                <Reason tone="warning" role="alert">
                  {messageForError(t, savedQuery.error)}
                </Reason>
                <Button onClick={() => void savedQuery.refetch()}>{t("common.retry")}</Button>
              </div>
            ) : null}
            {startError ? (
              <Reason tone="warning" role="alert">
                {startErrorText(t, startError)}
              </Reason>
            ) : null}
          </div>
        ) : null}
      </div>
      {canView ? (
        <div className="g-tasks__toolbar">
          <Segmented label={t("tasks.viewsLabel")} options={viewOptions} value={search.vista ?? "oggi"} onChange={(value) => update({ vista: value === "oggi" ? undefined : (value as TaskViewParam) })} />
          <div className="g-tasks__filters">
            <Select
              id={`${baseId}-kind`}
              label={t("tasks.kind")}
              value={search.cosa ?? "tutto"}
              options={kindOptions}
              onChange={(value) => update({ cosa: kindParamOf(value) })}
            />
            {byPerson ? (
              <Select id={`${baseId}-who`} label={t("tasks.who")} value={search.chi ?? "all"} options={personOptions} onChange={(value) => update({ chi: value === "all" ? undefined : value })} />
            ) : null}
            <Select
              id={`${baseId}-sort`}
              label={t("tasks.sort")}
              value={search.ordine ?? "fare"}
              options={sortOptions}
              onChange={(value) => update({ ordine: value === "priorita" || value === "lead" || value === "status" ? value : undefined })}
            />
          </div>
        </div>
      ) : null}
      <div className="g-tasks__results" aria-busy={canView && shown === null && !pages.isError ? true : undefined}>
        {results}
      </div>
      {other ? <OtherSessionDialog other={other} id={baseId} /> : null}
    </div>
  );
}

function TasksProblem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  return (
    <div className="g-tasks__problem">
      <Reason tone="warning" role="alert">
        {messageForError(t, error)}
      </Reason>
      <Button onClick={onRetry}>{t("common.retry")}</Button>
    </div>
  );
}

type RowProps = {
  row: TaskRow;
  view: TasksQuery["view"];
  byPerson: boolean;
  /** «Chiama» al posto di «Apri»: un Task aperto, per chi chiama da questa scheda. */
  callable: boolean;
  /** Con la chiamata del browser occupata: il motivo sopra la tabella, e «Chiama» è spento. */
  busyReasonId: string | null;
  /** Il parametro `da` della scheda del Lead, per «Torna ai Task». */
  source: string;
  timeZone: string;
  /** Quando l'agenda è stata letta (millisecondi): «Fatta adesso» si misura da lì. */
  readAt: number;
};

/** Il tono di «Quando»: scaduto in rosso, oggi in giallo come nel mock-up, i prossimi giorni neutri. */
function dueTone(row: TaskRow, view: TasksQuery["view"]): Tone {
  if (row.dueKind === "overdue") return "danger";
  return view === "today" ? "warning" : "neutral";
}

/**
 * «Completati»: quando è stato fatto, con l'esito della chiamata che l'ha fatto. La riga porta l'ultima chiamata del Lead, non
 * quella del richiamo: il suo esito vale soltanto se è nata prima del completamento (dopo ce n'è stata un'altra, e l'esito di
 * quella del richiamo non si sa da qui). Un richiamo segnato fatto a mano non ha esito.
 */
function doneText(t: Translate, row: TaskRow, timeZone: string): string | null {
  const { task, lead } = row;
  if (task.completedAt === null) return null;
  const when = formatNumericDateTime(timeZone, task.completedAt);
  const latest = lead.latestCall;
  const outcome =
    task.completion === "called" && latest?.outcome && Date.parse(latest.at) <= Date.parse(task.completedAt) ? outcomeLabel(t, latest.outcome) : null;
  return outcome ? t("tasks.doneAt", { when, outcome }) : when;
}

function TaskRowView({ row, view, byPerson, callable, busyReasonId, source, timeZone, readAt }: RowProps) {
  const t = useT();
  // «Chiama» e «Apri» dicono di quale Lead sono: il nome come descrizione.
  const nameId = useId();
  const { task, lead } = row;
  const done = view === "done";
  const due = formatNumericDateTime(timeZone, task.dueAt);
  const overdue = !done && row.dueKind === "overdue";
  const what =
    task.type !== "callback" && task.appointment
      ? t("appointments.tasks.what", { kind: t(`appointments.kind.${task.type}`), when: due, minutes: task.appointment.minutes })
      : [t("tasks.whatCallback", { when: due }), task.agreedWithLead ? t("tasks.agreed") : null].filter(Boolean).join(" · ");
  // Disco e demo non si chiamano dai Task: «Apri» porta alla scheda, dove si segna l'esito; il Meet, se Google l'ha dato.
  const callsHere = callable && task.type === "callback";
  const meetUrl = task.status === "open" ? (task.appointment?.meetUrl ?? null) : null;
  const finished = done ? doneText(t, row, timeZone) : null;
  const doneNow = done && task.completedAt !== null && readAt - Date.parse(task.completedAt) < DONE_NOW_MS;
  const link = { to: "/lead/$leadId", params: { leadId: lead.id }, search: { da: source } } as const;

  return (
    <tr data-overdue={overdue ? "true" : undefined}>
      <td data-label={t("tasks.columns.when")}>
        {done ? (
          <>
            <StatusPill tone="success" icon="check">
              {doneNow ? t("tasks.doneNow") : (finished ?? "—")}
            </StatusPill>
            {doneNow && finished ? <span className="g-table__sub">{finished}</span> : null}
          </>
        ) : (
          <StatusPill tone={dueTone(row, view)} icon="clock">
            {[due, overdue ? t("tasks.overdue") : null].filter(Boolean).join(" · ")}
          </StatusPill>
        )}
      </td>
      <td data-label={t("tasks.columns.what")}>
        <strong className="g-tasks__what">{what}</strong>
        {!done && task.priority === "high" ? (
          <span className="g-pill g-tasks__priority" data-tone="warning">
            {t("tasks.priorityHigh")}
          </span>
        ) : null}
      </td>
      <th scope="row">
        {row.position === null ? (
          <TaskLead row={row} link={link} nameId={nameId} />
        ) : (
          // Il posto nella cascata a sinistra del nome, come nella Smart List; chi usa uno screen reader sente «Posto 1».
          <span className="g-lead-table__ordered">
            <span className="g-lead-table__position" title={t("tasks.start.position", { position: row.position })}>
              <span className="g-visually-hidden">{t("smartLists.page.positionPrefix")} </span>
              {row.position}
            </span>
            <span className="g-lead-table__ordered-lead">
              <TaskLead row={row} link={link} nameId={nameId} />
            </span>
          </span>
        )}
      </th>
      <td data-label={t("tasks.columns.city")}>{cityCell(t, lead.city)}</td>
      <td data-label={t("tasks.columns.status")}>{statusCell(lead.status)}</td>
      <td data-label={t("tasks.columns.latestCall")}>{latestCallCell(t, timeZone, lead.latestCall)}</td>
      {byPerson ? (
        done ? (
          <td data-label={t("tasks.columns.owner")}>{lead.owner?.displayName ?? "—"}</td>
        ) : (
          <td data-label={t("tasks.columns.assignee")}>{task.assignee.displayName}</td>
        )
      ) : null}
      <td className="g-tasks__action">
        {meetUrl ? (
          <a className="g-button" href={meetUrl} target="_blank" rel="noopener noreferrer" aria-describedby={nameId}>
            {t("appointments.tasks.meet")}
          </a>
        ) : null}
        {callsHere && task.status === "open" && busyReasonId !== null ? (
          <Button disabled aria-describedby={`${nameId} ${busyReasonId}`}>
            <Icon name="phone" />
            {t("tasks.call")}
          </Button>
        ) : callsHere && task.status === "open" ? (
          <Link {...link} state={{ callFromTasks: task.id }} className="g-button" aria-describedby={nameId}>
            <Icon name="phone" />
            {t("tasks.call")}
          </Link>
        ) : (
          <Link {...link} className="g-button" aria-describedby={nameId}>
            {t("tasks.open")}
          </Link>
        )}
      </td>
    </tr>
  );
}

/** Il Lead della riga: il nome, che apre la scheda, e il salone sotto. */
function TaskLead({ row, link, nameId }: { row: TaskRow; link: { to: "/lead/$leadId"; params: { leadId: string }; search: { da: string } }; nameId: string }) {
  return (
    <>
      <Link {...link} id={nameId}>
        {row.lead.displayName ?? "—"}
      </Link>
      {row.lead.salonName ? <span className="g-table__sub">{row.lead.salonName}</span> : null}
    </>
  );
}
